import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Boxes, CheckCircle2, AlertTriangle, RefreshCw, Loader2,
  GitBranch, TriangleAlert, Square, TrendingUp, Activity, Zap,
  Database, ShoppingCart, ArrowLeftRight, Clock, ArrowUpRight, Sparkles,
  Search, ImageIcon, BarChart2, FileSpreadsheet, Target, ShieldCheck,
  BellRing, ChevronLeft, Info, XCircle, Star,
} from "lucide-react";
import OnboardingChecklist from "@/components/OnboardingChecklist";

/* ════════════════════════════════════════════
   HOOKS
═══════════════════════════════════════════ */
function useAnimatedCounter(target, duration = 1000) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    if (target === null || target === undefined) return;
    const from = prev.current;
    const to   = target;
    prev.current = to;
    if (from === to) { setVal(to); return; }
    const startTs = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTs) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setVal(Math.round(from + (to - from) * ease));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return val;
}

/* ════════════════════════════════════════════
   SCORE UTILS
═══════════════════════════════════════════ */
function scoreColor(s) {
  if (s === null || s === undefined) return { ring: "#cbd5e1", text: "text-[var(--text-muted)]", label: "—", bg: "bg-[var(--bg-card-hover)]", bar: "#cbd5e1" };
  if (s >= 80) return { ring: "#22c55e", text: "text-emerald-500", label: "ممتاز",     bg: "bg-emerald-50",  bar: "#22c55e" };
  if (s >= 60) return { ring: "#f59e0b", text: "text-amber-500",   label: "متوسط",     bg: "bg-amber-50",    bar: "#f59e0b" };
  if (s >= 40) return { ring: "#f97316", text: "text-orange-500",  label: "يحتاج عمل", bg: "bg-orange-50",  bar: "#f97316" };
  return              { ring: "#ef4444", text: "text-red-500",     label: "ضعيف",      bg: "bg-red-50",      bar: "#ef4444" };
}

function ScoreRing({ score, size = 80 }) {
  const r    = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(Math.max(score ?? 0, 0), 100);
  const dash = (pct / 100) * circ;
  const c    = scoreColor(pct);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={7} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={c.ring} strokeWidth={7} strokeLinecap="round"
        strokeDasharray={`${dash} ${circ - dash}`}
        style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.4,0,.2,1)" }}
      />
    </svg>
  );
}

/* ════════════════════════════════════════════
   SALLA STATUS PILL
═══════════════════════════════════════════ */
function SallaStatusPill({ status }) {
  const M = {
    healthy:                   { dot: "bg-emerald-400 animate-pulse", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200",  label: "متصل" },
    connected:                 { dot: "bg-emerald-400 animate-pulse", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200",  label: "متصل" },
    expiring_soon:             { dot: "bg-amber-400",                  text: "text-amber-700",   bg: "bg-amber-50 border-amber-200",      label: "ينتهي قريباً" },
    expired_will_auto_refresh: { dot: "bg-amber-400 animate-pulse",   text: "text-amber-700",   bg: "bg-amber-50 border-amber-200",      label: "يتجدد تلقائياً" },
    unknown_expiry:            { dot: "bg-emerald-400",                text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200",  label: "متصل" },
    needs_reauth:              { dot: "bg-red-500",                    text: "text-red-700",     bg: "bg-red-50 border-red-200",          label: "يحتاج إعادة ربط" },
    expired:                   { dot: "bg-red-500",                    text: "text-red-700",     bg: "bg-red-50 border-red-200",          label: "انتهت الصلاحية" },
    disconnected:              { dot: "bg-slate-400",                  text: "text-[var(--text-secondary)]",   bg: "bg-slate-100 border-[var(--border-color)]",     label: "غير متصل" },
    never_connected:           { dot: "bg-slate-300",                  text: "text-[var(--text-secondary)]",   bg: "bg-[var(--bg-card-hover)] border-[var(--border-color)]",      label: "لم يُربط بعد" },
  };
  const s = M[status] || M.never_connected;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════ */
const KPI_CFG = [
  { key: "total_products", label: "المنتجات",      icon: Package,       tone: "blue",    suffix: "منتج"  },
  { key: "total_stock",    label: "المخزون",        icon: Boxes,         tone: "slate",   suffix: "قطعة"  },
  { key: "success_rate",   label: "معدل النجاح",   icon: TrendingUp,    tone: "emerald", suffix: "%"     },
  { key: "failed_count",   label: "فشل التزامن",   icon: AlertTriangle, tone: "red",     suffix: ""      },
  { key: "low_stock_count",label: "مخزون منخفض",   icon: TriangleAlert, tone: "amber",   suffix: ""      },
  { key: "branches_count", label: "الفروع",         icon: GitBranch,     tone: "indigo",  suffix: ""      },
];
const TONES = {
  blue:    { bar: "#004EEB", icon: "bg-[#004EEB]/10 text-[#004EEB]",    val: "text-[#004EEB]"   },
  slate:   { bar: "#64748B", icon: "bg-slate-100 text-[var(--text-secondary)]",        val: "text-[var(--text-secondary)]"   },
  emerald: { bar: "#22C55E", icon: "bg-emerald-50 text-emerald-600",    val: "text-emerald-600" },
  red:     { bar: "#EF4444", icon: "bg-red-50 text-red-500",            val: "text-red-500"     },
  amber:   { bar: "#F59E0B", icon: "bg-amber-50 text-amber-600",        val: "text-amber-600"   },
  indigo:  { bar: "#6366F1", icon: "bg-indigo-50 text-indigo-600",      val: "text-indigo-600"  },
};
function KpiCard({ cfg, value }) {
  const t   = TONES[cfg.tone];
  const num = useAnimatedCounter(value ?? 0);
  return (
    <div data-testid={`kpi-${cfg.key}`}
      className="relative bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 overflow-hidden group shadow-sm hover:-translate-y-0.5 transition-transform">
      <div className="absolute bottom-0 inset-x-0 h-0.5 rounded-b-2xl transition-all duration-500 w-0 group-hover:w-full"
        style={{ background: t.bar }} />
      <cfg.icon className="absolute -bottom-1 -start-1 w-16 h-16 opacity-[0.04] text-[var(--text-primary)]" />
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <span className="text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase">{cfg.label}</span>
          <div className={`w-8 h-8 rounded-xl grid place-items-center text-sm ${t.icon} shrink-0`}>
            <cfg.icon className="w-4 h-4" />
          </div>
        </div>
        <div className="flex items-baseline gap-1">
          <span className={`text-[1.9rem] font-black tabular-nums leading-none ${t.val}`}>
            {value !== null && value !== undefined ? num.toLocaleString("en-US") : "—"}
          </span>
          {cfg.suffix && <span className="text-xs text-[var(--text-muted)]">{cfg.suffix}</span>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   ACTIVITY FEED
═══════════════════════════════════════════ */
const ACT_ICONS = {
  sync:   { icon: ArrowLeftRight, color: "text-[#004EEB]",  bg: "bg-[#004EEB]/10"  },
  seo:    { icon: Search,         color: "text-violet-600", bg: "bg-violet-50"      },
  import: { icon: FileSpreadsheet,color: "text-emerald-600",bg: "bg-emerald-50"     },
  alt:    { icon: ImageIcon,      color: "text-pink-600",   bg: "bg-pink-50"        },
};
const ACT_KIND_DOT = {
  success: "bg-emerald-400",
  error:   "bg-red-400",
  running: "bg-[#004EEB] pulse-dot",
  info:    "bg-blue-300",
  pending: "bg-slate-300",
};

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)  return "الآن";
    if (diff < 3600) return `${Math.round(diff/60)} د`;
    if (diff < 86400) return `${Math.round(diff/3600)} س`;
    return `${Math.round(diff/86400)} ي`;
  } catch { return ""; }
}

function ActivityFeed({ items, loading }) {
  if (loading) return (
    <div className="flex items-center justify-center py-10 gap-2 text-[var(--text-muted)]">
      <Loader2 className="w-5 h-5 animate-spin text-[#004EEB]" />
      <span className="text-sm">تحديث النشاط...</span>
    </div>
  );
  if (!items || items.length === 0) return (
    <div className="flex flex-col items-center gap-3 py-10 text-[var(--text-muted)]">
      <Activity className="w-8 h-8 text-slate-200" />
      <span className="text-sm">لا يوجد نشاط بعد</span>
    </div>
  );
  return (
    <div className="space-y-0" data-testid="activity-feed">
      {items.map((item, i) => {
        const cfg = ACT_ICONS[item.type] || ACT_ICONS.sync;
        const dot = ACT_KIND_DOT[item.kind] || ACT_KIND_DOT.pending;
        return (
          <div key={i}
            className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--bg-card-hover)]/60 transition-colors border-b border-slate-50 last:border-0"
            style={{ animationDelay: `${i * 40}ms` }}>
            {/* Timeline dot */}
            <div className="relative flex flex-col items-center mt-1">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
              {i < items.length - 1 && <div className="w-px flex-1 mt-1 bg-slate-100" style={{ minHeight: 24 }} />}
            </div>
            {/* Icon */}
            <div className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${cfg.bg}`}>
              <cfg.icon className={`w-3.5 h-3.5 ${cfg.color}`} />
            </div>
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-[var(--text-secondary)] truncate">{item.label}</span>
                <span className="text-[10px] text-[var(--text-muted)] shrink-0 mono">{fmtRelative(item.at)}</span>
              </div>
              {item.detail && (
                <p className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">{item.detail}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════
   AI INSIGHTS
═══════════════════════════════════════════ */
const INSIGHT_STYLES = {
  critical: { bg: "bg-red-50",     border: "border-red-100",     dot: "bg-red-500",     text: "text-red-700",     icon: XCircle,       iconC: "text-red-400"    },
  warning:  { bg: "bg-amber-50",   border: "border-amber-100",   dot: "bg-amber-500",   text: "text-amber-800",   icon: AlertTriangle, iconC: "text-amber-500"  },
  info:     { bg: "bg-blue-50",    border: "border-blue-100",    dot: "bg-blue-500",    text: "text-blue-800",    icon: Info,          iconC: "text-blue-400"   },
  success:  { bg: "bg-emerald-50", border: "border-emerald-100", dot: "bg-emerald-500", text: "text-emerald-800", icon: CheckCircle2,  iconC: "text-emerald-500"},
};

function InsightCard({ insight, navigate }) {
  const s = INSIGHT_STYLES[insight.type] || INSIGHT_STYLES.info;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${s.bg} ${s.border}`}>
      <s.icon className={`w-4 h-4 shrink-0 mt-0.5 ${s.iconC}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[12px] font-medium leading-relaxed ${s.text}`}>{insight.text}</p>
        {insight.action && insight.link && (
          <button onClick={() => navigate(insight.link)}
            className={`mt-1.5 text-[11px] font-bold underline underline-offset-2 ${s.text} flex items-center gap-0.5`}>
            {insight.action} <ChevronLeft className="w-3 h-3 rtl:rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   STORE STRENGTH
═══════════════════════════════════════════ */
function StrengthBar({ label, score, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(score ?? 0), 300 + delay);
    return () => clearTimeout(t);
  }, [score, delay]);

  const c = scoreColor(score);
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] text-[var(--text-secondary)] w-28 shrink-0 text-end">{label}</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%`, background: color || c.bar }} />
      </div>
      <span className={`text-[11px] font-black mono w-8 shrink-0 ${c.text}`}>
        {score !== null && score !== undefined ? score : "—"}
      </span>
    </div>
  );
}

/* ════════════════════════════════════════════
   BEFORE / AFTER CARD
═══════════════════════════════════════════ */
function BeforeAfterMetric({ label, before, after, diff, unit = "" }) {
  const afterNum  = useAnimatedCounter(after ?? 0, 1400);
  const beforeNum = useAnimatedCounter(before ?? 0, 1000);
  const improved  = diff !== null && diff !== undefined && diff > 0;
  const declined  = diff !== null && diff !== undefined && diff < 0;

  return (
    <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 shadow-sm relative overflow-hidden">
      {improved && (
        <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-500" />
      )}
      <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest mb-4">{label}</p>
      <div className="flex items-end justify-between gap-3">
        {/* Before */}
        <div className="text-center">
          <div className="text-[10px] text-[var(--text-muted)] mb-1">قبل</div>
          <div className="text-2xl font-black text-slate-300 mono tabular-nums leading-none">
            {before !== null && before !== undefined ? beforeNum : "—"}
            {before !== null && <span className="text-base">{unit}</span>}
          </div>
        </div>

        {/* Arrow + diff */}
        <div className="flex flex-col items-center gap-1">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-black ${
            improved ? "bg-emerald-50 text-emerald-600" :
            declined ? "bg-red-50 text-red-500" :
            "bg-[var(--bg-card-hover)] text-[var(--text-muted)]"
          }`}>
            {improved && <TrendingUp className="w-3 h-3" />}
            {diff !== null && diff !== undefined
              ? `${diff > 0 ? "+" : ""}${diff}`
              : "—"}
          </div>
          <div className="h-px w-8 bg-slate-200" />
        </div>

        {/* After */}
        <div className="text-center">
          <div className="text-[10px] text-[var(--text-muted)] mb-1">الآن</div>
          <div className={`text-2xl font-black mono tabular-nums leading-none ${
            after !== null ? (improved ? "text-emerald-600" : "text-[var(--text-primary)]") : "text-slate-300"
          }`}>
            {after !== null && after !== undefined ? afterNum : "—"}
            {after !== null && <span className="text-base">{unit}</span>}
          </div>
        </div>
      </div>

      {before === null && after !== null && (
        <p className="text-[10px] text-[var(--text-muted)] mt-3">أجرِ تحليلاً ثانياً لرؤية التحسّن</p>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   SYNC TIMELINE
═══════════════════════════════════════════ */
const SYNC_DOT = {
  success: "bg-emerald-500",
  failed:  "bg-red-500",
  partial: "bg-amber-400",
  running: "bg-[#004EEB] pulse-dot",
  pending: "bg-slate-300",
};
const SYNC_BADGE = {
  success: "bg-emerald-50 text-emerald-700",
  failed:  "bg-red-50 text-red-600",
  partial: "bg-amber-50 text-amber-700",
  running: "bg-[#004EEB]/8 text-[#004EEB]",
  pending: "bg-slate-100 text-[var(--text-secondary)]",
};
const SYNC_LABEL = { success: "نجح", failed: "فشل", partial: "جزئي", running: "نشط", pending: "بانتظار" };

/* ════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════ */
export default function Dashboard() {
  const navigate = useNavigate();
  const [kpis,   setKpis]   = useState(null);
  const [logs,   setLogs]   = useState([]);
  const [running,setRunning]= useState(false);
  const [syncing,setSyncing]= useState(false);
  const [seoAudit, setSeoAudit]  = useState(null);
  const [sallaHlth,setSallaHlth] = useState(null);
  const [intel,  setIntel]  = useState(null);   // activity, insights, strength, before_after
  const [intelLoading, setIntelLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [k, l, st, seo, salla] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/sync/logs", { params: { limit: 6 } }),
        api.get("/sync/status"),
        api.get("/seo/audit/latest").catch(() => ({ data: null })),
        api.get("/salla/health").catch(() => ({ data: null })),
      ]);
      setKpis(k.data);
      setLogs(l.data);
      setRunning(!!st.data?.running);
      setSeoAudit(seo.data);
      setSallaHlth(salla.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadIntel = useCallback(async () => {
    setIntelLoading(true);
    try {
      const { data } = await api.get("/dashboard/intelligence");
      setIntel(data);
    } catch { /* non-critical */ }
    finally { setIntelLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadIntel();
    const id = setInterval(load, 8000);
    const id2 = setInterval(loadIntel, 30000);
    return () => { clearInterval(id); clearInterval(id2); };
  }, [load, loadIntel]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("/sync/now");
      toast.success("بدأ التزامن");
      setTimeout(() => { load(); loadIntel(); }, 800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const stopSync = async () => {
    try {
      await api.post("/sync/cancel");
      toast.success("تم الإيقاف");
      setTimeout(load, 600);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const seoScore    = seoAudit?.score ?? null;
  const seoC        = scoreColor(seoScore);
  const strength    = intel?.strength;
  const beforeAfter = intel?.before_after;
  const insights    = intel?.insights || [];
  const activity    = intel?.activity || [];

  const isConnected = ["healthy", "connected", "unknown_expiry", "expiring_soon", "expired_will_auto_refresh"].includes(sallaHlth?.status);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      {running && <div className="h-[3px] running-bar fixed top-0 inset-x-0 z-50" />}

      <div className="p-5 md:p-7 max-w-[1440px] mx-auto space-y-5">

        <OnboardingChecklist />

        {/* ── Header ──────────────────────────────────────────────── */}
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`w-2 h-2 rounded-full ${running ? "bg-[#004EEB] pulse-dot" : "bg-emerald-400"}`} />
              <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--text-muted)] font-bold">
                {running ? "تزامن نشط" : "جاهز"}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text-primary)] leading-none">مركز التحكم</h1>
            <p className="text-[var(--text-muted)] text-sm mt-1">ذكاء تجاري لحظي — متابعة شاملة لأداء متجرك</p>
          </div>
          <div className="flex gap-2">
            {running && (
              <button onClick={stopSync} data-testid="dashboard-stop-button"
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5">
                <Square className="w-3.5 h-3.5 fill-current" /> إيقاف
              </button>
            )}
            <button onClick={triggerSync} disabled={syncing || running} data-testid="dashboard-sync-now-button"
              className="flex items-center gap-2 bg-[#004EEB] hover:bg-[#0040C1] disabled:opacity-60 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-lg shadow-[#004EEB]/20 transition-all hover:-translate-y-0.5 active:translate-y-0">
              {syncing || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {running ? "جارٍ التزامن…" : "تزامن الآن"}
            </button>
          </div>
        </header>

        {/* ── Top Status Row ──────────────────────────────────────── */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="bg-[var(--bg-card)] dark:bg-[#111C2E] rounded-2xl border border-[var(--border-color)] p-4 shadow-sm flex items-center gap-3 relative overflow-hidden">
            <div className="relative shrink-0">
              <div className="w-11 h-11 rounded-xl grid place-items-center"
                style={{ background: isConnected ? "rgba(34,197,94,.1)" : "rgba(148,163,184,.1)" }}>
                <ShoppingCart className="w-5 h-5" style={{ color: isConnected ? "#22c55e" : "#94a3b8" }} />
              </div>
              <div className={`absolute -top-1 -end-1 w-3 h-3 rounded-full border-2 border-white dark:border-[#111C2E] ${
                isConnected ? "bg-emerald-400" :
                sallaHlth?.status === "expiring_soon" ? "bg-amber-400 animate-pulse" : "bg-slate-300"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-wider">سلة</div>
              <div className="font-bold text-[var(--text-primary)] text-sm truncate">{sallaHlth?.store_name || "لم يُربط بعد"}</div>
              <div className="mt-1"><SallaStatusPill status={sallaHlth?.status || "never_connected"} /></div>
            </div>
            {!isConnected && (
              <button onClick={() => navigate("/settings")}
                className="shrink-0 text-[11px] font-bold text-[#004EEB] flex items-center gap-0.5 hover:underline">
                ربط <ArrowUpRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Sync Bridge */}
          <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-[#004EEB] to-indigo-400 opacity-70" />
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-wider">المزامنة</span>
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                running ? "bg-[#004EEB]/8 text-[#004EEB]" : "bg-emerald-50 text-emerald-700"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${running ? "bg-[#004EEB] pulse-dot" : "bg-emerald-400"}`} />
                {running ? "نشط الآن" : "مستقر"}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-xl bg-[#004EEB]/10 grid place-items-center">
                  <Database className="w-4 h-4 text-[#004EEB]" />
                </div>
                <span className="text-[10px] text-[var(--text-muted)]">قيود</span>
              </div>
              <div className="flex-1 relative mx-3 h-px bg-slate-200">
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <div className={`h-full ${running ? "bridge-flow-running" : "bridge-flow"}`} />
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-5 h-5 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-full grid place-items-center shadow-sm z-10">
                  <ArrowLeftRight className="w-2.5 h-2.5 text-[#004EEB]" />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-xl bg-violet-50 grid place-items-center">
                  <ShoppingCart className="w-4 h-4 text-violet-500" />
                </div>
                <span className="text-[10px] text-[var(--text-muted)]">سلة</span>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-[10px] text-[var(--text-muted)]">
              <Clock className="w-3 h-3" />
              <span>كل 15 دقيقة</span>
              {logs[0] && <span className="ms-auto">آخر: {new Date(logs[0].started_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
          </div>

          {/* SEO Quick Score */}
          <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 shadow-sm cursor-pointer hover:-translate-y-0.5 transition-transform"
            onClick={() => navigate("/optimizer")}>
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <ScoreRing score={seoScore ?? 0} size={72} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-base font-black mono ${seoC.text}`}>{seoScore ?? "—"}</span>
                  {seoScore != null && <span className="text-[8px] text-[var(--text-muted)]">/100</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-wider mb-0.5">صحة SEO</div>
                <div className={`text-sm font-black ${seoC.text}`}>{seoScore != null ? seoC.label : "غير محلَّل"}</div>
                <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                  {seoAudit?.products_with_issues
                    ? <span className="text-amber-600">{seoAudit.products_with_issues} منتج يحتاج تحسين</span>
                    : seoScore != null
                      ? <span className="text-emerald-600">جميع المنتجات ممتازة</span>
                      : "ابدأ التحليل الآن"}
                </div>
                <div className="flex items-center gap-1 mt-1.5 text-[11px] font-bold text-[#004EEB]">
                  {seoScore != null ? "تحسين SEO" : "بدء التحليل"}
                  <ArrowUpRight className="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── KPI Cards ───────────────────────────────────────────── */}
        <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {KPI_CFG.map(cfg => (
            <KpiCard key={cfg.key} cfg={cfg} value={kpis?.[cfg.key] ?? null} />
          ))}
        </section>

        {/* ── Before/After + Store Strength ───────────────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">

          {/* Before / After (2 cols) */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#004EEB]" />
              <h2 className="text-sm font-bold text-[var(--text-secondary)]">قبل وبعد التحسين</h2>
            </div>
            <BeforeAfterMetric
              label="درجة SEO"
              before={beforeAfter?.seo?.before}
              after={beforeAfter?.seo?.after}
              diff={beforeAfter?.seo?.diff}
            />
            <BeforeAfterMetric
              label="منتجات تحتاج تحسين"
              before={beforeAfter?.products_improved?.before}
              after={beforeAfter?.products_improved?.after}
              diff={beforeAfter?.products_improved?.diff != null ? -beforeAfter.products_improved.diff : null}
            />
            {!beforeAfter?.has_data && (
              <div className="bg-[var(--bg-card-hover)] border border-[var(--border-color)] rounded-xl p-4 text-center">
                <Target className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-[11px] text-[var(--text-muted)]">أجرِ أول تحليل SEO لتفعيل مقارنة قبل/بعد</p>
                <button onClick={() => navigate("/optimizer")}
                  className="mt-2 text-[11px] font-bold text-[#004EEB] hover:underline">
                  ابدأ التحليل →
                </button>
              </div>
            )}
          </div>

          {/* Store Strength (3 cols) */}
          <div className="lg:col-span-3 bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#004EEB]" />
                <h2 className="text-sm font-bold text-[var(--text-secondary)]">قوة المتجر الرقمية</h2>
              </div>
              {strength?.overall != null && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--text-muted)]">الإجمالي</span>
                  <span className={`text-lg font-black mono ${scoreColor(strength.overall).text}`}>
                    {strength.overall}%
                  </span>
                </div>
              )}
            </div>

            {strength?.has_data ? (
              <div className="space-y-3.5">
                {strength.categories.map((cat, i) => (
                  <StrengthBar key={cat.key} label={cat.label} score={cat.score} color={cat.color} delay={i * 150} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-8">
                <div className="w-12 h-12 rounded-2xl bg-[var(--bg-card-hover)] grid place-items-center">
                  <BarChart2 className="w-5 h-5 text-slate-300" />
                </div>
                <p className="text-sm text-[var(--text-muted)] text-center">أجرِ تحليل SEO لرؤية قوة متجرك</p>
                <button onClick={() => navigate("/optimizer")}
                  className="text-sm font-bold text-[#004EEB] hover:underline flex items-center gap-1">
                  ابدأ الآن <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Overall strength ring */}
            {strength?.has_data && (
              <div className="mt-5 pt-4 border-t border-slate-50 flex items-center gap-4">
                <div className="relative shrink-0">
                  <ScoreRing score={strength.overall ?? 0} size={60} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-sm font-black mono ${scoreColor(strength.overall).text}`}>
                      {strength.overall ?? "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className={`text-base font-black ${scoreColor(strength.overall).text}`}>
                    {scoreColor(strength.overall).label}
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
                    {(strength.overall ?? 0) >= 80
                      ? "متجرك في وضع ممتاز — استمر في التحسين"
                      : (strength.overall ?? 0) >= 60
                        ? "أداء جيد — هناك فرص تحسين متاحة"
                        : "يحتاج اهتماماً — افتح محرك النمو الآن"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Sync Log + AI Intelligence ─────────────────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Sync Log */}
          <div className="lg:col-span-2 bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
              <div>
                <h2 className="text-sm font-bold text-[var(--text-primary)]">سجل التزامن</h2>
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">آخر 6 عمليات</p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${running ? "bg-[#004EEB] pulse-dot" : "bg-emerald-400"}`} />
                <span className="text-[10px] text-[var(--text-muted)] font-semibold">{running ? "مباشر" : "مستقر"}</span>
              </div>
            </div>
            {logs.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-[var(--text-muted)]">
                <Activity className="w-7 h-7 text-slate-200" />
                <p className="text-sm">لا يوجد سجل — شغّل «تزامن الآن»</p>
              </div>
            ) : (
              <div data-testid="recent-logs" className="divide-y divide-slate-50">
                {logs.map((log) => {
                  const dot   = SYNC_DOT[log.status] || SYNC_DOT.pending;
                  const badge = SYNC_BADGE[log.status] || SYNC_BADGE.pending;
                  const lbl   = SYNC_LABEL[log.status] || "—";
                  return (
                    <div key={log.id} className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-card-hover)]/50 transition-colors">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                      <div className="w-20 shrink-0 text-[10px] text-[var(--text-muted)] mono">
                        {new Date(log.started_at).toLocaleString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit", hour12:false })}
                      </div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge}`}>{lbl}</span>
                      <span className="text-[11px] text-[var(--text-muted)] flex-1">
                        {log.trigger === "cron" ? "تلقائي" : "يدوي"}
                      </span>
                      <div className="flex gap-2 mono text-[11px] shrink-0">
                        <span className="text-emerald-600 font-bold">+{log.products_updated}</span>
                        <span className="text-red-400">−{log.products_failed}</span>
                        <span className="text-slate-300">/{log.products_total}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Panel: AI Insights + System Health */}
          <div className="space-y-3">
            {/* AI Insights */}
            <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm">
              <div className="px-4 py-3.5 border-b border-slate-50 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                <h2 className="text-sm font-bold text-[var(--text-secondary)]">رؤى AI</h2>
                {intelLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-300 ms-auto" />}
              </div>
              <div className="p-3 space-y-2">
                {insights.length > 0
                  ? insights.map((ins, i) => <InsightCard key={i} insight={ins} navigate={navigate} />)
                  : <div className="py-6 text-center text-[11px] text-[var(--text-muted)]">جارٍ التحليل...</div>
                }
              </div>
            </div>

            {/* Quick System Health */}
            <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 shadow-sm">
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-3">إحصائيات النظام</p>
              <div className="space-y-2.5">
                {[
                  { icon: CheckCircle2,  label: "نجاح التزامن",  val: kpis?.success_rate != null ? `${kpis.success_rate}%` : "—", c: "text-emerald-600" },
                  { icon: GitBranch,     label: "الفروع النشطة", val: kpis?.branches_count ?? "—", c: "text-indigo-600" },
                  { icon: TriangleAlert, label: "مخزون منخفض",  val: kpis?.low_stock_count ?? "—",
                    c: kpis?.low_stock_count > 0 ? "text-amber-600" : "text-[var(--text-muted)]", testid: "dashboard-kpi-low-stock" },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
                      <r.icon className="w-3.5 h-3.5 text-slate-300" />
                      {r.label}
                    </span>
                    <span className={`text-sm font-black mono ${r.c}`} data-testid={r.testid}>
                      {typeof r.val === "number" ? r.val.toLocaleString("en-US") : r.val}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Live Activity Feed ──────────────────────────────────── */}
        <section className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
              <h2 className="text-sm font-bold text-[var(--text-secondary)]">سجل النشاط الموحّد</h2>
              <span className="text-[10px] bg-slate-100 text-[var(--text-secondary)] px-2 py-0.5 rounded-full font-semibold">
                مباشر
              </span>
            </div>
            <button onClick={loadIntel} disabled={intelLoading}
              className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors disabled:opacity-40">
              <RefreshCw className={`w-3 h-3 ${intelLoading ? "animate-spin" : ""}`} />
              تحديث
            </button>
          </div>
          <ActivityFeed items={activity} loading={intelLoading && activity.length === 0} />
        </section>

        {/* ── محرك النمو Banner ───────────────────────────────────── */}
        <section>
          <div className="relative rounded-3xl overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0A0F1E 0%, #0F2460 55%, #004EEB 100%)" }}>
            <div className="absolute inset-0 opacity-[0.05]"
              style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.2) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
            <div className="relative px-6 md:px-10 py-7">
              <div className="flex flex-wrap items-center justify-between gap-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold"
                      style={{ background: "rgba(96,165,250,.15)", color: "#93C5FD", border: "1px solid rgba(96,165,250,.2)" }}>
                      <Sparkles className="w-3 h-3" /> محرك النمو AI
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-black text-white">حسّن متجرك بالذكاء الاصطناعي</h2>
                  <p className="text-sm text-blue-200/70 mt-1">تحليل PageSpeed · تحسين SEO · نص ALT للصور · تحويل WebP</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { icon: BarChart2,   label: "PageSpeed",      sub: "سرعة التحميل"       },
                    { icon: Search,      label: "محرك SEO",        sub: "العناوين والأوصاف"  },
                    { icon: ImageIcon,   label: "ALT للصور",       sub: "AI Vision"           },
                    { icon: Zap,         label: "ضغط WebP",        sub: "توفير 70%"           },
                  ].map(f => (
                    <button key={f.label} onClick={() => navigate("/optimizer")}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:-translate-y-0.5"
                      style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)" }}>
                      <div className="w-6 h-6 rounded-lg grid place-items-center" style={{ background: "rgba(96,165,250,.15)" }}>
                        <f.icon className="w-3 h-3 text-blue-300" />
                      </div>
                      <div className="text-start">
                        <div className="text-[11px] font-bold text-white/90">{f.label}</div>
                        <div className="text-[10px] text-blue-200/60">{f.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => navigate("/optimizer")}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[var(--bg-card)] text-[#004EEB] rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 shadow-xl shadow-black/20 shrink-0">
                  افتح محرك النمو <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
